/**
 * i18n Configuration
 *
 * Internationalization setup using i18next and react-i18next.
 * Supports 30 languages covering all major European languages plus Thai and Chinese.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// #3452: only English (the fallback) is bundled. The other 29 locales were
// imported statically here — 6.6 MB of JSON inside the main chunk, every
// visitor downloading 30 languages to read one. They now load on demand as
// their own chunks through the tiny backend below; `partialBundledLanguages`
// tells i18next that `en` is already present while the rest comes from the
// backend. The locale files themselves are untouched (npm run i18n:check
// still validates them against en.json).
import en from './locales/en.json';

type LocaleModule = { default: Record<string, unknown> };

/** One dynamic import per locale → one chunk per locale (Vite needs literal paths). */
export const LOCALE_LOADERS: Record<string, () => Promise<LocaleModule>> = {
  bg: () => import('./locales/bg.json'),
  ca: () => import('./locales/ca.json'),
  cs: () => import('./locales/cs.json'),
  da: () => import('./locales/da.json'),
  de: () => import('./locales/de.json'),
  el: () => import('./locales/el.json'),
  es: () => import('./locales/es.json'),
  et: () => import('./locales/et.json'),
  fi: () => import('./locales/fi.json'),
  fr: () => import('./locales/fr.json'),
  hr: () => import('./locales/hr.json'),
  hu: () => import('./locales/hu.json'),
  it: () => import('./locales/it.json'),
  lt: () => import('./locales/lt.json'),
  lv: () => import('./locales/lv.json'),
  nb: () => import('./locales/nb.json'),
  nl: () => import('./locales/nl.json'),
  pl: () => import('./locales/pl.json'),
  pt: () => import('./locales/pt.json'),
  ro: () => import('./locales/ro.json'),
  ru: () => import('./locales/ru.json'),
  sk: () => import('./locales/sk.json'),
  sl: () => import('./locales/sl.json'),
  sr: () => import('./locales/sr.json'),
  sv: () => import('./locales/sv.json'),
  th: () => import('./locales/th.json'),
  tr: () => import('./locales/tr.json'),
  uk: () => import('./locales/uk.json'),
  zh: () => import('./locales/zh.json'),
};

/**
 * Minimal i18next backend: resolves a language to its lazy chunk. Region
 * codes from the browser detector (`de-DE`, `pt-BR`) read the base language's
 * bundle, so the detected code is kept as-is for Intl formatting. An unknown
 * or unloadable locale reports an error and i18next falls back to English.
 */
const lazyLocaleBackend = {
  type: 'backend' as const,
  init() {
    /* nothing to configure */
  },
  read(lng: string, _ns: string, callback: (err: unknown, data?: Record<string, unknown> | false) => void) {
    const base = lng.toLowerCase().split('-')[0];
    if (base === 'en') {
      callback(null, en as Record<string, unknown>);
      return;
    }
    const loader = LOCALE_LOADERS[base];
    if (!loader) {
      callback(new Error(`[i18n] no bundle for locale "${lng}"`), false);
      return;
    }
    loader()
      .then((m) => callback(null, m.default))
      .catch((err) => callback(err, false));
  },
};

const resources = {
  en: { translation: en },
};

/**
 * Resolves once the detected language's bundle is loaded (or has failed and
 * English took over). main.tsx waits on it (bounded) before the first render
 * so a German user never sees an English flash on cold start.
 */
export const i18nReady: Promise<unknown> = i18n
  .use(LanguageDetector)
  .use(lazyLocaleBackend)
  .use(initReactI18next)
  .init({
    resources,
    partialBundledLanguages: true,
    fallbackLng: 'en',
    defaultNS: 'translation',

    // Language detection options
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    react: {
      useSuspense: false, // Disable suspense to avoid loading states
      // Re-render when a lazily fetched bundle lands, not only on a switch.
      bindI18n: 'languageChanged loaded',
    },
  })
  .catch((err: unknown) => {
    // init() only rejects on a hard failure; a missing bundle is a logged
    // warning and the app continues in English.
    console.error('[i18n] init failed', err);
  });

export default i18n;

/**
 * Change the current language
 */
export const changeLanguage = (lng: string) => {
  i18n.changeLanguage(lng);
  localStorage.setItem('i18nextLng', lng);
};

/**
 * Get current language
 */
export const getCurrentLanguage = () => i18n.language;

/**
 * Supported languages
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'bg', name: 'Bulgarian', nativeName: 'Български' },
  { code: 'ca', name: 'Catalan', nativeName: 'Català' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'et', name: 'Estonian', nativeName: 'Eesti' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių' },
  { code: 'lv', name: 'Latvian', nativeName: 'Latviešu' },
  { code: 'nb', name: 'Norwegian', nativeName: 'Norsk bokmål' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina' },
  { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina' },
  { code: 'sr', name: 'Serbian', nativeName: 'Српски' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
] as const;
